import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { signIn, confirmSignIn } from 'aws-amplify/auth';
import { buildQrCodeSvg } from '../utils/qrCode.ts';
import ThemeToggle from './ThemeToggle.tsx';
import { BrandLogoMark, BrandWordmark } from './BrandLogo.tsx';
import './Login.css';
import LegalLinks from './LegalLinks.tsx';

// ---------------------------------------------------------------------------
// Sign-in stages
// ---------------------------------------------------------------------------
//
// Cognito sign-in is a state machine, not a single call: `signIn` and every
// subsequent `confirmSignIn` return a `nextStep.signInStep` telling us what the
// user has to do next. Amplify v6 defines fourteen such steps.
//
// This screen used to track a single `requiresNewPassword` boolean and let
// every other step fall through silently — the form simply sat there with no
// error and no state change. That was survivable while the user pool had MFA
// off. It is not survivable now: with `mfa_configuration = "ON"` and TOTP as
// the only factor, EVERY user hits CONTINUE_SIGN_IN_WITH_TOTP_SETUP on their
// first sign-in and CONFIRM_SIGN_IN_WITH_TOTP_CODE on every one after, so a
// silent fall-through would lock out 100% of users.
//
// Hence: an explicit stage union, one central `applyNextStep` mapper, and a
// loud, named error for anything unmapped.
export type Stage =
  | 'credentials'
  | 'newPassword'
  | 'totpSetup'
  | 'totpCode'
  | 'mfaSetupSelection';

// Used as the `issuer`/label in the otpauth:// URI, i.e. what the authenticator
// app shows next to the rotating code.
//
// Keep this simple. Amplify's `getSetupUri` interpolates it into the URI
// WITHOUT URL-encoding, so a ':' or '?' in this string would corrupt the URI and
// break every scan.
const TOTP_ISSUER = 'ORchestrate';

const TOTP_CODE_LENGTH = 6;

// The exact strings Cognito accepts when the user is choosing which factor to
// enrol, mapped to the label we show for each.
//
// 'SMS' is deliberately absent: Amplify throws on it, despite its own (stale)
// JSDoc claiming otherwise.
//
// 'EMAIL' is deliberately absent too, and this is NOT an oversight — do not
// re-add it without also implementing the steps it leads to. Cognito accepts
// 'EMAIL' here, but the step it returns next is CONTINUE_SIGN_IN_WITH_EMAIL_SETUP
// (and thereafter CONFIRM_SIGN_IN_WITH_EMAIL_CODE / CONFIRM_SIGN_IN_WITH_MFA_SELECTION),
// none of which this screen implements. Offering the button would promise a
// route that dead-ends in the "cannot handle yet" error AFTER the user has
// already committed to it. Listing only what we can finish means an
// email-only pool fails at the point of the decision, with an explanation,
// instead of one screen later.
const SELECTABLE_MFA_TYPES: Record<string, string> = {
  TOTP: 'Authenticator app',
};

/**
 * True for every stage that is reached only AFTER `signIn` has succeeded, i.e.
 * every stage where a Cognito challenge is already in flight.
 *
 * SECURITY: this is what decides whether an error returns the user to a clean
 * first screen. Once a challenge is in flight, Amplify's `signInStore` holds a
 * `challengeName` that we cannot read and cannot roll back, so a failed step
 * leaves the local screen and Cognito's idea of the conversation out of sync.
 * Staying put would keep a form mounted whose submit sends the wrong kind of
 * secret for the challenge Cognito now expects — the password-leak vector
 * described on `friendlyError`. 'credentials' is excluded because a rejected
 * password should leave the user on the sign-in form to correct it.
 */
const isChallengeStage = (stage: Stage): boolean => stage !== 'credentials';

/**
 * The two stages whose only input is the 6-digit authenticator code.
 *
 * Used to scope the "a rejected code empties the field" behaviour in `runStep`:
 * no other stage owns `totpCode`, and a failure elsewhere must never wipe input
 * the user still needs.
 */
const isTotpStage = (stage: Stage): boolean => stage === 'totpSetup' || stage === 'totpCode';

/**
 * The ONLY error names whose SDK-supplied `err.message` we render verbatim.
 *
 * This is an ALLOWLIST, and it has to stay one. A denylist can only suppress
 * the leak carriers we already know about (`SignInException`, below); it cannot
 * suppress the one a future SDK release invents. Every error name that is not
 * in this set gets copy we wrote, so a message we have never read cannot reach
 * the screen — see `genericFailure`.
 *
 * `InvalidPasswordException` earns its place: Cognito populates that message
 * with the pool's own configured password policy ("Password must have uppercase
 * characters"), which is precisely what the user needs to fix the problem and
 * cannot be reproduced accurately from our side. It is also raised by the policy
 * validator BEFORE any challenge response is echoed anywhere, so it carries no
 * secret material.
 */
const VERBATIM_MESSAGE_ERROR_NAMES: ReadonlySet<string> = new Set(['InvalidPasswordException']);

/**
 * Error NAMES are a fixed vocabulary of SDK class-name constants, never
 * interpolated from anything the user typed — unlike messages. We still
 * shape-check before rendering one, so a future SDK that starts stuffing detail
 * into `name` cannot slip a sentence (or a secret) through this route.
 */
const SAFE_ERROR_NAME = /^[A-Za-z][A-Za-z0-9]{0,63}$/;

/**
 * The SDK's own message, but only when the error's name is on the allowlist.
 * Returns null otherwise, so every call site has to supply its own fallback and
 * an un-allowlisted name structurally cannot render vendor text.
 */
function verbatimMessage(err: any): string | null {
  if (!VERBATIM_MESSAGE_ERROR_NAMES.has(err?.name)) return null;
  const message = typeof err?.message === 'string' ? err.message.trim() : '';
  return message.length > 0 ? message : null;
}

/**
 * Copy for an error we have not positively decided is safe to quote. The name
 * is included because it is what lets an administrator diagnose the problem
 * from a screenshot — the same reason `applyNextStep` names an unhandled step.
 */
function genericFailure(name: string): string {
  const suffix = 'Please try again, or contact your administrator if it keeps happening.';
  return SAFE_ERROR_NAME.test(name)
    ? `Something went wrong (${name}). ${suffix}`
    : `Something went wrong. ${suffix}`;
}

/**
 * Maps Cognito/Amplify exceptions to copy we control.
 *
 * SECURITY — the `SignInException` case is the whole reason this function
 * exists and must not be "simplified" into the default branch.
 *
 * Amplify's `handleMFASetupChallenge` ends with an unconditional
 *
 *     throw new AuthError({ name: 'SignInException',
 *       message: `Cannot proceed with MFA setup using challengeResponse: ${challengeResponse}` })
 *
 * reached whenever the pending challenge is MFA_SETUP and the challengeResponse
 * is not 'EMAIL', not 'TOTP', not all digits and does not contain '@'. A
 * password is exactly such a value, and it is embedded VERBATIM and
 * un-redacted in `err.message`, `String(err)` and `err.stack`. Rendering
 * `err.message` would paint the user's plaintext password onto the login
 * screen (and into any screenshot or error report taken from it).
 *
 * So: never render `err.message` for SignInException. The structural defence —
 * each stage submitting only its own field — is in the handlers below; this is
 * the second line of it.
 *
 * That case is a named suppression of a KNOWN carrier. The allowlist above is
 * the general form of the same defence: `default:` used to return
 * `err?.message`, which meant any future Amplify error that happened to embed a
 * challenge response would have been printed on sight. Vendor strings are now
 * opt-in, one error name at a time.
 */
export function friendlyError(err: any, stage: Stage): string {
  const name: string = typeof err?.name === 'string' ? err.name : '';

  switch (name) {
    case 'SignInException':
      // NEVER surface err.message here. See the note above.
      return 'That sign-in step could not be completed. Please start over and sign in again.';

    case 'CodeMismatchException':
    case 'EnableSoftwareTokenMFAException':
      return 'That code is not correct. Authenticator codes change every 30 seconds — enter the one showing right now.';

    case 'ExpiredCodeException':
      return 'That code has expired. Enter the code currently showing in your authenticator app.';

    case 'NotAuthorizedException':
      // On the first screen this is simply a bad password. Once a challenge is
      // in flight the same exception means the 3-minute Cognito auth session
      // has lapsed, which is unrecoverable — the user must sign in again.
      return stage === 'credentials'
        ? 'Incorrect email or password. Please try again.'
        : 'Your sign-in session has expired. Please start over and sign in again.';

    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return 'Too many attempts. Please wait a little while and try again.';

    case 'InvalidPasswordException':
      // Allowlisted for verbatim rendering. `verbatimMessage` re-checks the
      // allowlist rather than trusting this case label, so adding a `case` here
      // without adding the name above yields OUR copy, not the vendor's.
      return verbatimMessage(err) ?? 'That password does not meet the requirements.';

    default:
      return genericFailure(name);
  }
}

const Login: React.FC = () => {
  const [stage, setStage] = useState<Stage>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpSecret, setTotpSecret] = useState<string | null>(null);
  const [totpSetupUri, setTotpSetupUri] = useState<string | null>(null);
  const [allowedMfaTypes, setAllowedMfaTypes] = useState<string[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Bumped every time a TOTP submission is REJECTED, to drive the focus effect
  // below. A counter rather than a boolean because two rejections in a row must
  // each move focus, and a boolean would already be set the second time.
  const [totpRejectionCount, setTotpRejectionCount] = useState(0);
  const totpInputRef = useRef<HTMLInputElement | null>(null);
  const { user, loading, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as any)?.from?.pathname || '/';
  const busy = loading || submitting;

  // If user is already logged in, redirect them
  useEffect(() => {
    if (user && !loading) {
      navigate(from, { replace: true });
    }
  }, [user, loading, navigate, from]);

  // A rejected code empties the field (see `runStep`); this puts the caret back
  // in it, so the next rotating code can simply be typed. Without it focus stays
  // on the submit button the user just clicked.
  useEffect(() => {
    if (totpRejectionCount === 0) return;
    // The input carries `disabled={busy}` while a submit is in flight, and a
    // disabled element cannot take focus — so wait for `busy` to clear. The ref
    // is null when the failure sent us back to the credentials screen, which is
    // exactly when focus should NOT be moved.
    if (busy) return;
    totpInputRef.current?.focus();
  }, [totpRejectionCount, busy]);

  // Forget every secret the user typed. Called the moment credentials stop
  // being needed, so that no later handler — and no React DevTools reader —
  // can find a password sitting in component state while an MFA challenge is
  // on screen.
  const forgetSecrets = () => {
    setPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  /**
   * NO SETTLE DELAY — and that is a decision, not an omission.
   *
   * The new-password submit used to pass `settleMs = 1000` here ("give Amplify a
   * beat to persist the session") while every other path passed 0. That
   * asymmetry could not survive MFA: the TOTP-code screen is now how EVERY
   * returning user signs in, so if the beat were load-bearing it would be needed
   * there most of all, and a second of dead time on the busiest path is not
   * something to pay on a hunch.
   *
   * It is not load-bearing. Evidence, in aws-amplify 6.16.0 as installed:
   *
   *  - `providers/cognito/apis/confirmSignIn.mjs` `await`s `cacheCognitoTokens(...)`
   *    BEFORE it returns `{ isSignedIn: true, nextStep: { signInStep: 'DONE' } }`.
   *    `signInWithSRP.mjs` / `signInWithUserPassword.mjs` do the same on the
   *    no-challenge path.
   *  - `cacheCognitoTokens` awaits `tokenOrchestrator.setTokens(...)` →
   *    `TokenStore.storeTokens(...)`, which awaits every `keyValueStorage.setItem`
   *    for the access, id and refresh tokens.
   *
   * So a resolved `isSignedIn: true` already means the tokens are written; there
   * is nothing left to wait for. Note also that the plain-password sign-in path
   * has ALWAYS navigated with no delay, using that same code — the timeout only
   * ever covered one of the two callers of the identical SDK routine.
   *
   * And if a write ever did lag, a fixed sleep is the wrong shape of fix:
   * `AuthContext.checkUser` already retries twice with a 500ms backoff on the
   * other side of this navigation, which costs nothing when it is not needed.
   */
  const completeSignIn = async () => {
    forgetSecrets();
    setTotpCode('');
    // Deliberately a full browser navigation rather than react-router: it
    // forces AuthContext to re-run checkUser against the freshly stored tokens.
    window.location.href = from;
  };

  // Return to a clean first screen. Used when an error is unrecoverable (an
  // expired Cognito auth session) and when the user asks to start over.
  const resetToCredentials = () => {
    setStage('credentials');
    setTotpCode('');
    setTotpSecret(null);
    setTotpSetupUri(null);
    setAllowedMfaTypes([]);
    forgetSecrets();
  };

  /**
   * The single place a `signIn`/`confirmSignIn` result is turned into UI state.
   * Every handler routes through here, so there is exactly one list of handled
   * steps and exactly one behaviour for the unhandled ones.
   *
   * SECURITY INVARIANT: every path out of this function either moves `stage`
   * forward or calls `resetToCredentials()`. None of them may leave the caller's
   * form mounted, because by the time we are here the resolved call has ALREADY
   * advanced Amplify's stored `challengeName` — a resubmit of that same form
   * would send the previous stage's secret into the next stage's challenge.
   */
  const applyNextStep = async (result: any): Promise<void> => {
    const step: string | undefined = result?.nextStep?.signInStep;

    if (result?.isSignedIn || step === 'DONE') {
      await completeSignIn();
      return;
    }

    switch (step) {
      case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
        setStage('newPassword');
        return;

      case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP': {
        const details = result?.nextStep?.totpSetupDetails;
        const sharedSecret: string | undefined = details?.sharedSecret;

        let uri: string | null = null;
        try {
          // getSetupUri returns a URL OBJECT, not a string. Interpolating the
          // object straight into JSX would render "[object Object]".
          uri = details?.getSetupUri?.(TOTP_ISSUER, email)?.toString() ?? null;
        } catch {
          uri = null;
        }

        if (!sharedSecret && !uri) {
          setLocalError(
            'We could not start authenticator setup — the server did not return a setup key. Please sign in again, or contact your administrator.'
          );
          // Back to the first screen, NOT back to whatever form we came from:
          // Cognito's challenge has already advanced to MFA_SETUP, so the
          // previous form's submit would now send the wrong secret.
          resetToCredentials();
          return;
        }

        forgetSecrets();
        setTotpCode('');
        setTotpSecret(sharedSecret ?? null);
        setTotpSetupUri(uri);
        setStage('totpSetup');
        return;
      }

      case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
        forgetSecrets();
        setTotpCode('');
        setStage('totpCode');
        return;

      case 'CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION': {
        const allowed: string[] = (result?.nextStep?.allowedMFATypes ?? []).filter(
          (type: string) => type in SELECTABLE_MFA_TYPES
        );

        if (allowed.length === 0) {
          setLocalError(
            'This account must set up multi-factor authentication, but no supported method is available. Please contact your administrator.'
          );
          resetToCredentials();
          return;
        }

        forgetSecrets();
        setAllowedMfaTypes(allowed);
        setStage('mfaSetupSelection');
        return;
      }

      case 'RESET_PASSWORD':
        forgetSecrets();
        setStage('credentials');
        setLocalError(
          'Your password must be reset before you can sign in. Use the "Forgot password?" link below to set a new one.'
        );
        return;

      default:
        // No silent fall-through, ever. Naming the step is what lets an
        // administrator diagnose a pool configuration change from a screenshot.
        setLocalError(
          `This account requires a sign-in step this app cannot handle yet (${step ?? 'unknown step'}). Please contact your administrator.`
        );
        resetToCredentials();
    }
  };

  const runStep = async (stageForErrors: Stage, action: () => Promise<any>) => {
    setSubmitting(true);
    try {
      const result = await action();
      // SECURITY: a RESOLVED signIn/confirmSignIn has already consumed whatever
      // secret it was handed, and Cognito has already advanced the challenge.
      // Keeping that secret in React state past this point is never useful and
      // is exactly what lets a second submit re-send it into the NEXT challenge
      // (see friendlyError's note on SignInException). So drop it here, before
      // any branch of applyNextStep can decide to leave a form on screen.
      forgetSecrets();
      await applyNextStep(result);
    } catch (err: any) {
      setLocalError(friendlyError(err, stageForErrors));

      // A REJECTED code is the ordinary failure on these screens — digits
      // mistyped, or a code that rotated between being read and being typed —
      // and the copy answers it with "enter the one showing right now". The user
      // could not: the field still held the six digits Cognito had just refused,
      // and both `maxLength={TOTP_CODE_LENGTH}` on the input and the 6-character
      // slice in `onTotpCodeChange` then DISCARD every further keystroke. The
      // box was full, so the screen was dead until it was cleared by hand.
      //
      // Clearing it (and, via the effect above, putting the caret back in it) is
      // what makes "try the next code" the single action the copy promises.
      //
      // Scoped to the TOTP stages deliberately: `totpCode` belongs to no other
      // stage, and an unrelated failure must not wipe a field the user is still
      // filling in.
      if (isTotpStage(stageForErrors)) {
        setTotpCode('');
        setTotpRejectionCount((count) => count + 1);
      }

      // An expired auth session or a rejected MFA-setup challenge cannot be
      // retried from the current screen — Cognito has discarded the session.
      // This covers 'newPassword' as well as the TOTP stages: the new-password
      // screen is exactly where a user spends the minutes it takes Cognito's
      // 3-minute auth session to lapse.
      if (
        isChallengeStage(stageForErrors) &&
        (err?.name === 'NotAuthorizedException' || err?.name === 'SignInException')
      ) {
        resetToCredentials();
      }
    } finally {
      setSubmitting(false);
    }
  };

  // --- Stage handlers -------------------------------------------------------
  //
  // SECURITY: keeping a password out of an MFA_SETUP challenge takes THREE
  // defences together, and each handler below is only the first of them.
  //
  //  1. Each handler reads ONLY its own field and refuses to run unless that
  //     stage is the one currently on screen (the `stage !== ...` guards below).
  //  2. `runStep` calls `forgetSecrets()` the instant a call RESOLVES, so a
  //     password cannot survive into the stage that follows it. The guard in (1)
  //     alone is not enough: a form whose stage never advanced still passes it.
  //  3. Every path out of `applyNextStep` either advances `stage` or calls
  //     `resetToCredentials()`, so no form stays mounted after the challenge it
  //     belonged to has been answered.
  //
  // (2) and (3) exist because Amplify advances its stored `challengeName` on a
  // RESOLVED call. A branch that showed an error and returned — leaving the
  // new-password form mounted and populated — would let a second click send the
  // plaintext password into `handleMFASetupChallenge`, which transmits it to AWS
  // as ChallengeResponses.EMAIL (password containing '@'), as
  // VerifySoftwareToken.UserCode (all-numeric password), or echoes it verbatim
  // in a SignInException message. See friendlyError's note on SignInException.

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (stage !== 'credentials' || submitting) return;

    if (!email || !password) {
      setLocalError('Please enter both email and password');
      return;
    }

    await runStep('credentials', () => signIn({ username: email, password }));
  };

  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (stage !== 'newPassword' || submitting) return;

    if (!newPassword || !confirmPassword) {
      setLocalError('Please enter and confirm your new password');
      return;
    }

    if (newPassword !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setLocalError('Password must be at least 8 characters long');
      return;
    }

    // No settle delay: see the note on `completeSignIn` for why the 1000ms that
    // used to be passed here was removed rather than copied to the TOTP submits.
    await runStep('newPassword', () => confirmSignIn({ challengeResponse: newPassword }));
  };

  // Shared by both TOTP screens: enrolment (VerifySoftwareToken) and ordinary
  // sign-in (RespondToAuthChallenge) both take the same 6 digits.
  const submitTotpCode = async (stageForErrors: Stage) => {
    setLocalError(null);
    if (stage !== stageForErrors || submitting) return;

    if (totpCode.length !== TOTP_CODE_LENGTH) {
      setLocalError(`Enter the ${TOTP_CODE_LENGTH}-digit code from your authenticator app.`);
      return;
    }

    await runStep(stageForErrors, () => confirmSignIn({ challengeResponse: totpCode }));
  };

  const handleTotpSetupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitTotpCode('totpSetup');
  };

  const handleTotpCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitTotpCode('totpCode');
  };

  const handleMfaTypeSelect = async (mfaType: string) => {
    setLocalError(null);
    if (stage !== 'mfaSetupSelection' || submitting) return;

    // Only the literal strings 'TOTP' and 'EMAIL' are accepted here.
    await runStep('mfaSetupSelection', () => confirmSignIn({ challengeResponse: mfaType }));
  };

  const onTotpCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Authenticator codes are digits only; strip anything else so a pasted
    // "123 456" or a stray space cannot be mistaken for a bad code.
    setTotpCode(e.target.value.replace(/\D/g, '').slice(0, TOTP_CODE_LENGTH));
  };

  // Recomputed only when the URI changes; QR generation is pure arithmetic but
  // there is no reason to redo it on every keystroke in the code field.
  const qr = useMemo(() => {
    if (!totpSetupUri) return null;
    try {
      return buildQrCodeSvg(totpSetupUri);
    } catch {
      // The manual-entry secret below is the fallback, so a failed QR is
      // degraded service rather than a dead end.
      return null;
    }
  }, [totpSetupUri]);

  const heading = () => {
    switch (stage) {
      case 'newPassword':
        return <>Set new <em>password</em></>;
      case 'totpSetup':
        return <>Set up <em>authentication</em></>;
      case 'totpCode':
        return <>Verify it's <em>you</em></>;
      case 'mfaSetupSelection':
        return <>Choose a <em>method</em></>;
      default:
        return <>Welcome <em>back</em></>;
    }
  };

  const totpCodeField = (id: string) => (
    <div className="form-group">
      <label htmlFor={id}>{TOTP_CODE_LENGTH}-digit code</label>
      <input
        id={id}
        ref={totpInputRef}
        data-testid="totp-code-input"
        type="text"
        className="form-control totp-code-input"
        value={totpCode}
        onChange={onTotpCodeChange}
        placeholder="000000"
        disabled={busy}
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        maxLength={TOTP_CODE_LENGTH}
        autoFocus
      />
    </div>
  );

  // The ways OUT of a challenge screen, shown on all of them.
  //
  // "Forgot password?" used to live only on the credentials form, which is the
  // one screen where the user is not stuck. Someone halted at TOTP setup — no
  // authenticator app on this phone, an enrolment they abandoned days ago, a
  // password they now realise they cannot remember for the next attempt — had a
  // code field, a submit button, and nothing else. Recovery has to be reachable
  // from where the user actually is.
  const challengeFooter = (
    <div className="challenge-help" data-testid="challenge-help">
      <div className="challenge-help-actions">
        <button
          type="button"
          className="login-link"
          onClick={resetToCredentials}
          disabled={busy}
          data-testid="start-over-button"
        >
          Start over
        </button>
        <span className="challenge-help-sep" aria-hidden="true">·</span>
        <Link
          to="/forgot-password"
          className="login-link"
          data-testid="challenge-forgot-password"
        >
          Forgot password?
        </Link>
      </div>
      <p className="challenge-help-note" data-testid="challenge-contact-admin">
        Still stuck? Contact your administrator.
      </p>
    </div>
  );

  return (
    <div className="login-container">
      <div className="login-card">
        <ThemeToggle className="theme-toggle--login" />
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '18px' }}>
          <BrandLogoMark size={64} />
        </div>
        <h1 className="login-brand-title"><BrandWordmark /> · OR Allocation</h1>
        <h2>{heading()}</h2>

        {(localError || error) && (
          <div className="error-message" role="alert" data-testid="login-error">
            {localError || error}
          </div>
        )}

        {stage === 'credentials' && (
          <form onSubmit={handleSubmit} data-testid="credentials-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                disabled={busy}
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                className="form-control"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                disabled={busy}
                autoComplete="current-password"
              />
            </div>

            <div className="forgot-password-row">
              <Link to="/forgot-password" className="login-link">
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={busy}
            >
              {busy ? 'Logging in...' : 'Login'}
            </button>
          </form>
        )}

        {stage === 'newPassword' && (
          <form onSubmit={handleNewPasswordSubmit} data-testid="new-password-form">
            <p className="info-message">
              Please set a new permanent password for your account.
            </p>

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                id="newPassword"
                type="password"
                className="form-control"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                disabled={busy}
                autoComplete="new-password"
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                id="confirmPassword"
                type="password"
                className="form-control"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                disabled={busy}
                autoComplete="new-password"
              />
            </div>

            <div className="password-requirements">
              <p>Password must:</p>
              <ul>
                <li>Be at least 8 characters long</li>
                <li>Contain uppercase and lowercase letters</li>
                <li>Contain at least one number</li>
                <li>Contain at least one special character</li>
              </ul>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={busy}
            >
              {busy ? 'Setting Password...' : 'Set New Password'}
            </button>

            {/* Composing a password is where a user is most likely to burn
                through Cognito's 3-minute auth session. Without this the only
                control on screen is "Set New Password", which resubmits into a
                dead session forever and can only be escaped by reloading. */}
            {challengeFooter}
          </form>
        )}

        {stage === 'totpSetup' && (
          <form onSubmit={handleTotpSetupSubmit} data-testid="totp-setup-form">
            <p className="info-message">
              Your account is protected with an authenticator app. Set it up once
              here — after that you'll enter a 6-digit code each time you sign in.
            </p>

            <ol className="totp-steps">
              <li>
                Install an authenticator app on your phone if you don't have one
                (Google Authenticator, Microsoft Authenticator, Authy, or 1Password
                all work).
              </li>
              <li>Scan the square code below with that app.</li>
              <li>Enter the 6-digit code the app shows you.</li>
            </ol>

            {/* Cognito mints a BRAND NEW shared secret on every MFA_SETUP
                challenge, and the otpauth label is identical each time. Anyone
                who scanned and then abandoned enrolment is already carrying an
                "ORchestrate" entry that can never produce a code Cognito will
                accept — and is about to acquire a second, indistinguishable one.
                Nothing on the phone distinguishes the dead entry from the live
                one, so it has to be said here, before the scan. */}
            <p className="totp-stale-entry-note" data-testid="totp-stale-entry-note">
              Already have an <strong>{TOTP_ISSUER}</strong> entry from an earlier
              attempt? Delete it before you scan — this creates a new key, and the
              old entry's codes will never work.
            </p>

            {qr ? (
              <div className="totp-qr" data-testid="totp-qr">
                <svg
                  viewBox={`0 0 ${qr.viewBoxSize} ${qr.viewBoxSize}`}
                  width="200"
                  height="200"
                  role="img"
                  aria-label="QR code for authenticator app setup"
                  shapeRendering="crispEdges"
                >
                  {/* Explicit light background: scanners need dark-on-light, and
                      the app's default theme is dark. */}
                  <rect x="0" y="0" width={qr.viewBoxSize} height={qr.viewBoxSize} fill="#ffffff" />
                  <path d={qr.path} fill="#000000" />
                </svg>
              </div>
            ) : (
              <p className="info-message" data-testid="totp-qr-unavailable">
                The QR code could not be displayed. Use the setup key below to add
                the account to your authenticator app by hand.
              </p>
            )}

            {/* The manual-entry key is REQUIRED, not a nicety: if the camera or
                the QR fails, this is the only way for the user to enrol. */}
            <div className="totp-secret-block">
              <p className="totp-secret-label">Can't scan? Enter this setup key by hand:</p>
              <code className="totp-secret" data-testid="totp-shared-secret">
                {totpSecret || 'unavailable'}
              </code>
              <p className="totp-secret-hint">
                Account: {email || 'your account'} · Issuer: {TOTP_ISSUER} · Type:
                time-based
              </p>
            </div>

            {totpSetupUri && (
              <p className="totp-uri-row">
                <a
                  className="login-link"
                  href={totpSetupUri}
                  data-testid="totp-setup-uri"
                >
                  Open in an authenticator app on this device
                </a>
              </p>
            )}

            {totpCodeField('totpSetupCode')}

            <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
              {busy ? 'Verifying...' : 'Verify and finish setup'}
            </button>

            {challengeFooter}
          </form>
        )}

        {stage === 'totpCode' && (
          <form onSubmit={handleTotpCodeSubmit} data-testid="totp-code-form">
            <p className="info-message">
              Open your authenticator app and enter the {TOTP_CODE_LENGTH}-digit
              code it is showing for {TOTP_ISSUER}.
            </p>

            {totpCodeField('totpSignInCode')}

            <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
              {busy ? 'Verifying...' : 'Verify code'}
            </button>

            {challengeFooter}
          </form>
        )}

        {stage === 'mfaSetupSelection' && (
          <div data-testid="mfa-setup-selection">
            <p className="info-message">
              Your account needs a second sign-in factor. Choose how you'd like to
              receive it.
            </p>

            <div className="mfa-options">
              {allowedMfaTypes.map((mfaType) => (
                <button
                  key={mfaType}
                  type="button"
                  className="btn btn-primary btn-block"
                  onClick={() => handleMfaTypeSelect(mfaType)}
                  disabled={busy}
                  data-testid={`mfa-option-${mfaType}`}
                >
                  {SELECTABLE_MFA_TYPES[mfaType]}
                </button>
              ))}
            </div>

            {challengeFooter}
          </div>
        )}

        <div className="login-footer">
          <p>Please contact your administrator to create an account.</p>
          <LegalLinks />
        </div>
      </div>
    </div>
  );
};

export default Login;
